import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { adminApi, adminAuth, API } from "../lib/api";
import { NaaviLogo, ShapeCircle } from "../components/brand/Shapes";
import {
  LogOut, Search, Download, Users, CalendarDays, Mail,
  MessageSquare, TrendingUp, ChevronRight, ChevronDown, Filter, ExternalLink,
  Shield, UserPlus, KeyRound, Trash2, Copy, Check, GraduationCap, Lock,
  LayoutDashboard, Inbox, UserCircle2, Receipt, Megaphone, Settings as SettingsIcon,
  Menu as MenuIcon, X as XIcon, BookOpen, Utensils, Newspaper, Clipboard,
} from "lucide-react";
import StudentsPanel from "./admin/StudentsPanel";
import AttendancePanel from "./admin/AttendancePanel";
import UpdatesPanel from "./admin/UpdatesPanel";
import StaffAttendancePanel from "./admin/StaffAttendancePanel";
import SchoolSettingsPanel from "./admin/SchoolSettingsPanel";
import TermReportsBulkPanel from "./admin/TermReportsBulkPanel";
import MenuPanel from "./admin/MenuPanel";
import NewslettersPanel from "./admin/NewslettersPanel";
import SettingsPanel from "./admin/SettingsPanel";
import LeaveRequestsPanel from "./admin/LeaveRequestsPanel";
import ReportsPanel from "./admin/ReportsPanel";
import CalendarPanel from "./admin/CalendarPanel";
import SchoolCalendarPdfPanel from "./admin/SchoolCalendarPdfPanel";
import { toast } from "sonner";

const PROGRAM_LABELS = {
  toddlers: "Toddlers (2–6)",
  nursery: "Early Years 1 (3–4)",
  "pre-k": "Early Years 2 (4–5)",
  kindergarten: "Early Years 3 (5–6)",
};

const STATUS_COLORS = {
  new: "bg-[#648DE5]/15 text-[#0F1B47] border-[#648DE5]/30",
  contacted: "bg-[#C6D947]/30 text-[#0F1B47] border-[#C6D947]/60",
  visited: "bg-[#5BC4B8]/20 text-[#0F1B47] border-[#5BC4B8]/40",
  enrolled: "bg-[#0F1B47] text-[#FDFDF9] border-[#0F1B47]",
  closed: "bg-[#F5F5F0] text-[#4A5568] border-[#0F1B47]/15",
  confirmed: "bg-[#5BC4B8]/20 text-[#0F1B47] border-[#5BC4B8]/40",
  attended: "bg-[#0F1B47] text-[#FDFDF9] border-[#0F1B47]",
  cancelled: "bg-red-100 text-red-700 border-red-200",
};

const ADM_STATUSES = ["new", "contacted", "visited", "enrolled", "closed"];
const VISIT_STATUSES = ["confirmed", "attended", "cancelled"];

const PIE_COLORS = ["#648DE5", "#C6D947", "#5BC4B8", "#3AB0D6", "#0F1B47", "#1a2b6b"];

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { studentId: deepLinkStudentId } = useParams();
  const [tab, setTab] = useState(deepLinkStudentId ? "students" : "overview");
  const [me, setMe] = useState(null);
  const [stats, setStats] = useState(null);
  const [admissions, setAdmissions] = useState([]);
  const [visits, setVisits] = useState([]);
  const [newsletter, setNewsletter] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [staff, setStaff] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [showChangePw, setShowChangePw] = useState(false);

  const isSuper = me?.role === "superadmin";
  const can = (p) => isSuper || !!me?.permissions?.[p];

  useEffect(() => {
    if (!adminAuth.get()) {
      navigate("/admin/login");
      return;
    }
    loadAll();
  }, []); // eslint-disable-line

  const loadAll = async () => {
    try {
      setLoading(true);
      const meRes = await adminApi.get("/admin/me");
      setMe(meRes.data);
      const c = (perm) => meRes.data.role === "superadmin" || meRes.data.permissions?.[perm];
      const tasks = [adminApi.get("/admin/stats")];
      tasks.push(c("view_admissions") ? adminApi.get("/admin/admissions") : Promise.resolve({ data: [] }));
      tasks.push(c("view_visits") ? adminApi.get("/admin/visits") : Promise.resolve({ data: [] }));
      tasks.push(c("view_newsletter") ? adminApi.get("/admin/newsletter") : Promise.resolve({ data: [] }));
      tasks.push(c("view_messages") ? adminApi.get("/admin/contact") : Promise.resolve({ data: [] }));
      tasks.push(c("manage_attendance") ? adminApi.get("/admin/leave-requests") : Promise.resolve({ data: [] }));
      const [s, a, v, n, m, lr] = await Promise.all(tasks);
      setStats(s.data);
      setAdmissions(a.data);
      setVisits(v.data);
      setNewsletter(n.data);
      setContacts(m.data);
      setLeaves(lr.data || []);
      if (meRes.data.role === "superadmin") {
        const st = await adminApi.get("/admin/staff");
        setStaff(st.data);
      }
    } catch (e) {
      // 401 handled by interceptor
    } finally {
      setLoading(false);
    }
  };

  const reloadStaff = async () => {
    try {
      const st = await adminApi.get("/admin/staff");
      setStaff(st.data);
    } catch {}
  };

  const logout = () => {
    adminAuth.clear();
    navigate("/admin/login");
  };

  const filteredAdmissions = useMemo(() => {
    const q = search.trim().toLowerCase();
    return admissions
      .filter((a) => statusFilter === "all" || (a.status || "new") === statusFilter)
      .filter(
        (a) =>
          !q ||
          a.student_name?.toLowerCase().includes(q) ||
          a.father_name?.toLowerCase().includes(q) ||
          a.mother_name?.toLowerCase().includes(q) ||
          a.email?.toLowerCase().includes(q) ||
          a.mobile?.toLowerCase().includes(q)
      );
  }, [admissions, search, statusFilter]);

  const filteredVisits = useMemo(() => {
    const q = search.trim().toLowerCase();
    return visits
      .filter((v) => statusFilter === "all" || (v.status || "confirmed") === statusFilter)
      .filter(
        (v) =>
          !q ||
          v.parent_name?.toLowerCase().includes(q) ||
          v.email?.toLowerCase().includes(q) ||
          v.phone?.toLowerCase().includes(q)
      );
  }, [visits, search, statusFilter]);

  const updateAdmissionStatus = async (id, status) => {
    try {
      const { data } = await adminApi.patch(`/admin/admissions/${id}/status`, { status });
      setAdmissions((arr) => arr.map((a) => (a.id === id ? data : a)));
      if (selected?.id === id) setSelected(data);
      toast.success(`Status updated to "${status}"`);
    } catch {
      toast.error("Could not update status.");
    }
  };

  const updateVisitStatus = async (id, status) => {
    try {
      const { data } = await adminApi.patch(`/admin/visits/${id}/status`, { status });
      setVisits((arr) => arr.map((v) => (v.id === id ? data : v)));
      toast.success(`Visit status updated`);
    } catch {
      toast.error("Could not update status.");
    }
  };

  const downloadCSV = (path, filename) => {
    const token = adminAuth.get();
    fetch(`${API}${path}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = filename;
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(url);
        toast.success("Export downloaded.");
      })
      .catch(() => toast.error("Export failed."));
  };

  // Sidebar groups — order = display order. Each item maps to an existing tab key.
  const navGroups = useMemo(() => {
    const groups = [
      {
        id: "dashboard",
        section: false,
        items: [{ id: "overview", label: "Overview", icon: LayoutDashboard, show: true }],
      },
      {
        id: "school-admin",
        section: "SCHOOL ADMIN",
      },
      {
        id: "admissions-grp",
        label: "Admissions",
        icon: Inbox,
        items: [
          { id: "admissions", label: `Enquiries${admissions.length ? ` · ${admissions.length}` : ""}`, show: can("view_admissions") },
          { id: "visits", label: `Campus Visits${visits.length ? ` · ${visits.length}` : ""}`, show: can("view_visits") },
          { id: "subscribers", label: "Subscribers", show: can("view_newsletter") },
          { id: "messages", label: "Messages", show: can("view_messages") },
        ],
      },
      {
        id: "students-grp",
        label: "Students",
        icon: GraduationCap,
        items: [
          { id: "students", label: "All Students", show: can("view_students") },
          { id: "attendance", label: "Attendance", show: can("manage_attendance") || can("view_students") },
          { id: "term-reports-bulk", label: "Term Reports (Bulk)", show: can("manage_term_reports") },
          { id: "updates", label: "Weekly Updates", show: can("manage_updates") || can("view_updates") || can("view_students") },
          { id: "leave-requests", label: "Leave Requests", show: can("manage_attendance") },
          { id: "students-archive", label: "Archive", show: can("view_students") },
        ],
      },
      {
        id: "fees-grp",
        label: "Fees",
        icon: Receipt,
        items: [
          { id: "settings", label: "Fee Heads", show: can("manage_fees") },
          { id: "reports", label: "Reports & Tracking", show: can("view_receipts") },
        ],
      },
      {
        id: "comms-grp",
        label: "Communications",
        icon: Megaphone,
        items: [
          { id: "calendar", label: "Events & Holidays", icon: CalendarDays, show: can("manage_calendar") },
          { id: "school-calendar", label: "School Calendar PDF", icon: CalendarDays, show: can("manage_calendar") },
          { id: "newsletter", label: "Monthly Newsletter", icon: Newspaper, show: can("manage_newsletter") },
          { id: "menu", label: "Weekly Menu", icon: Utensils, show: can("manage_menu") },
        ],
      },
      {
        id: "staff-grp",
        label: "Staff",
        icon: Users,
        items: [
          { id: "staff", label: "All Staff", show: isSuper },
          { id: "staff-attendance", label: "Attendance", show: can("manage_attendance") || can("export_attendance") },
          { id: "school-settings", label: "School Settings", show: isSuper },
        ],
      },
    ];
    // Filter out groups with no visible items (keep section labels even if empty for now)
    return groups
      .map((g) => {
        if (g.section !== undefined) return g;
        const visible = (g.items || []).filter((it) => it.show !== false);
        return { ...g, items: visible };
      })
      .filter((g) => g.section !== undefined ? true : (g.items && g.items.length > 0));
  }, [admissions.length, visits.length, isSuper, me]); // eslint-disable-line

  // Active group helper (auto-expand the group that owns the current tab)
  const activeGroupId = useMemo(() => {
    for (const g of navGroups) {
      if (!g.items) continue;
      if (g.items.some((it) => it.id === tab)) return g.id;
    }
    return null;
  }, [navGroups, tab]);

  const [openGroups, setOpenGroups] = useState({});
  useEffect(() => {
    if (activeGroupId) setOpenGroups((p) => ({ ...p, [activeGroupId]: true }));
  }, [activeGroupId]);

  const [sidebarOpen, setSidebarOpen] = useState(false); // mobile drawer

  const activeLabel = useMemo(() => {
    for (const g of navGroups) {
      if (!g.items) continue;
      const hit = g.items.find((it) => it.id === tab);
      if (hit) return hit.label.replace(/\s·\s\d+$/, "");
    }
    return "Dashboard";
  }, [navGroups, tab]);

  return (
    <div className="min-h-screen bg-[#F5F5F0] flex" data-testid="admin-dashboard">
      {/* Sidebar — desktop fixed, mobile drawer */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-30 bg-[#0F1B47]/40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
      <aside
        className={`fixed lg:sticky lg:top-0 inset-y-0 left-0 z-40 w-72 bg-[#0F1B47] text-[#FDFDF9] flex flex-col transition-transform duration-200 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        } lg:translate-x-0 lg:h-screen`}
        data-testid="admin-sidebar"
      >
        {/* Brand */}
        <div className="px-5 py-5 border-b border-white/8 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="admin-sidebar-brand">
            <div className="bg-[#FDFDF9] rounded-xl px-2 py-1.5">
              <NaaviLogo height={28} />
            </div>
            <div className="leading-tight">
              <div className="text-[10px] font-bold tracking-[0.22em] uppercase text-[#C6D947]">Naavi</div>
              <div className="text-[11px] text-white/65">Admin Console</div>
            </div>
          </Link>
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden p-2 rounded-full text-white/70 hover:bg-white/10"
            aria-label="Close sidebar"
          >
            <XIcon size={16} />
          </button>
        </div>

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1" data-testid="admin-nav">
          {navGroups.map((g) => {
            if (g.section === false) {
              // Top-level single item (Overview / Staff)
              return (g.items || []).map((it) => {
                const ItIcon = it.icon;
                const isActive = tab === it.id;
                return (
                  <button
                    key={it.id}
                    onClick={() => { setTab(it.id); setSidebarOpen(false); }}
                    className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm font-semibold transition-colors ${
                      isActive
                        ? "bg-[#C6D947]/15 text-[#C6D947]"
                        : "text-white/80 hover:text-white hover:bg-white/5"
                    }`}
                    data-testid={`admin-tab-${it.id}`}
                  >
                    {ItIcon && <ItIcon size={16} className={isActive ? "text-[#C6D947]" : "text-white/60"} />}
                    {it.label}
                  </button>
                );
              });
            }
            if (typeof g.section === "string") {
              return (
                <div key={g.id} className="pt-5 pb-1.5 px-3 text-[10px] font-bold tracking-[0.24em] uppercase text-white/40">
                  {g.section}
                </div>
              );
            }
            // Group with collapsible sub-items
            const isOpen = !!openGroups[g.id];
            const hasActive = g.items.some((it) => it.id === tab);
            const GroupIcon = g.icon;
            return (
              <div key={g.id}>
                <button
                  onClick={() => setOpenGroups((p) => ({ ...p, [g.id]: !p[g.id] }))}
                  className={`w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-xl text-sm font-semibold transition-colors ${
                    hasActive ? "text-[#FDFDF9]" : "text-white/80 hover:text-white hover:bg-white/5"
                  }`}
                  data-testid={`admin-group-${g.id}`}
                  aria-expanded={isOpen}
                >
                  <span className="flex items-center gap-2.5">
                    {GroupIcon && <GroupIcon size={16} className={hasActive ? "text-[#C6D947]" : "text-white/60"} />}
                    {g.label}
                  </span>
                  {isOpen
                    ? <ChevronDown size={14} className="text-white/50" />
                    : <ChevronRight size={14} className="text-white/50" />}
                </button>
                {isOpen && (
                  <ul className="mt-1 mb-2 ml-3 pl-3 border-l border-white/10 space-y-0.5">
                    {g.items.map((it) => (
                      <li key={it.id}>
                        <button
                          onClick={() => { setTab(it.id); setSidebarOpen(false); }}
                          className={`w-full text-left px-3 py-2 rounded-lg text-[13px] transition-colors ${
                            tab === it.id
                              ? "bg-[#C6D947]/15 text-[#C6D947] font-semibold"
                              : "text-white/65 hover:text-white hover:bg-white/5"
                          }`}
                          data-testid={`admin-tab-${it.id}`}
                        >
                          {it.label}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </nav>

        {/* Footer / user pill */}
        <div className="px-3 pb-4 pt-2 border-t border-white/8">
          {me && (
            <div className="flex items-center gap-2.5 px-3 py-2 rounded-xl bg-white/5">
              <div className="w-8 h-8 rounded-full bg-[#C6D947]/25 text-[#C6D947] flex items-center justify-center text-[12px] font-bold">
                {(me.name || me.email || "A").slice(0, 1).toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[12px] font-semibold truncate text-white">{me.name || me.email}</div>
                <div className="text-[10px] tracking-[0.16em] uppercase text-white/50">
                  {isSuper ? "Superadmin" : "Staff"}
                </div>
              </div>
            </div>
          )}
        </div>
      </aside>

      {/* Right side — top bar + main */}
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="sticky top-0 z-30 bg-[#FDFDF9]/90 backdrop-blur-xl border-b border-[#0F1B47]/8">
          <div className="px-5 md:px-8 py-3.5 flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden p-2 -ml-2 rounded-full hover:bg-[#0F1B47]/5"
                aria-label="Open sidebar"
                data-testid="admin-sidebar-toggle"
              >
                <MenuIcon size={18} />
              </button>
              <div className="min-w-0">
                <div className="text-[10px] font-bold tracking-[0.22em] uppercase text-[#648DE5]">{activeLabel}</div>
                <div className="font-display text-lg md:text-xl text-[#0F1B47] font-medium truncate" data-testid="admin-greeting">
                  Hello, <span className="italic font-normal">{me?.name || (me?.email || "").split("@")[0] || "there"}</span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Link
                to="/"
                className="hidden sm:inline-flex items-center gap-2 text-sm font-semibold text-[#0F1B47] px-4 py-2 rounded-full hover:bg-[#0F1B47]/5"
                data-testid="admin-back-to-site"
              >
                <ExternalLink size={14} />
                View site
              </Link>
              <button
                onClick={() => setShowChangePw(true)}
                className="inline-flex items-center gap-2 text-sm font-semibold text-[#0F1B47] px-4 py-2 rounded-full hover:bg-[#0F1B47]/5"
                data-testid="admin-change-password-btn"
                title="Change password"
              >
                <Lock size={14} />
                <span className="hidden sm:inline">Change password</span>
              </button>
              <button
                onClick={logout}
                className="inline-flex items-center gap-2 text-sm font-semibold text-[#0F1B47] px-4 py-2 rounded-full bg-[#0F1B47]/5 hover:bg-[#0F1B47] hover:text-[#FDFDF9] transition-colors"
                data-testid="admin-logout"
              >
                <LogOut size={14} />
                <span className="hidden sm:inline">Sign out</span>
              </button>
            </div>
          </div>
        </header>

        <main className="flex-1 px-5 md:px-8 py-8">
          {loading ? (
          <div className="text-[#4A5568] text-sm py-20 text-center">Loading…</div>
        ) : (
          <>
            {tab === "overview" && (
              <Overview stats={stats} admissions={admissions} visits={visits} leaves={leaves} onJumpToLeaves={() => setTab("leave-requests")} canLeaves={can("manage_attendance")} />
            )}

            {tab === "admissions" && (
              <SectionWrap
                title="Admissions enquiries"
                subtitle="Every family who's enquired with Naavi."
                onExport={can("export_csv") ? () => downloadCSV("/admin/admissions.csv", "naavi-admissions.csv") : null}
                search={search}
                setSearch={setSearch}
                statuses={ADM_STATUSES}
                statusFilter={statusFilter}
                setStatusFilter={setStatusFilter}
              >
                <AdmissionsTable
                  rows={filteredAdmissions}
                  onSelect={setSelected}
                  onStatus={can("edit_admissions") ? updateAdmissionStatus : null}
                />
              </SectionWrap>
            )}

            {tab === "visits" && (
              <SectionWrap
                title="Campus visit bookings"
                subtitle="Scheduled tours, oldest at the bottom."
                onExport={can("export_csv") ? () => downloadCSV("/admin/visits.csv", "naavi-visits.csv") : null}
                search={search}
                setSearch={setSearch}
                statuses={VISIT_STATUSES}
                statusFilter={statusFilter}
                setStatusFilter={setStatusFilter}
              >
                <VisitsTable rows={filteredVisits} onStatus={can("edit_visits") ? updateVisitStatus : null} />
              </SectionWrap>
            )}

            {tab === "subscribers" && (
              <SectionWrap
                title="Newsletter subscribers"
                subtitle="Letters from Naavi list."
              >
                <SimpleTable
                  rows={newsletter}
                  columns={[
                    { key: "email", label: "Email" },
                    { key: "created_at", label: "Subscribed", fmt: fmtDate },
                  ]}
                  testid="newsletter-table"
                />
              </SectionWrap>
            )}

            {tab === "messages" && (
              <SectionWrap
                title="Contact messages"
                subtitle="Quick messages from the contact form."
              >
                <SimpleTable
                  rows={contacts}
                  columns={[
                    { key: "name", label: "Name" },
                    { key: "email", label: "Email" },
                    { key: "message", label: "Message", clamp: true },
                    { key: "created_at", label: "Received", fmt: fmtDate },
                  ]}
                  testid="messages-table"
                />
              </SectionWrap>
            )}

            {tab === "students" && can("view_students") && (
              <StudentsPanel can={can} openStudentId={deepLinkStudentId} />
            )}

            {tab === "students-archive" && can("view_students") && (
              <StudentsPanel can={can} mode="archive" />
            )}

            {tab === "attendance" && can("view_students") && (
              <AttendancePanel can={can} />
            )}

            {tab === "staff-attendance" && (can("manage_attendance") || can("export_attendance")) && (
              <StaffAttendancePanel can={can} isSuper={isSuper} />
            )}

            {tab === "term-reports-bulk" && can("manage_term_reports") && (
              <TermReportsBulkPanel />
            )}

            {tab === "school-settings" && isSuper && (
              <SchoolSettingsPanel />
            )}

            {tab === "updates" && (can("manage_updates") || can("view_updates") || can("view_students")) && (
              <UpdatesPanel can={can} />
            )}

            {tab === "menu" && can("manage_menu") && (
              <MenuPanel can={can} />
            )}

            {tab === "newsletter" && can("manage_newsletter") && (
              <NewslettersPanel can={can} />
            )}

            {tab === "staff" && isSuper && (
              <StaffPanel
                me={me}
                staff={staff}
                onRefresh={reloadStaff}
              />
            )}

            {tab === "settings" && can("manage_fees") && (
              <SettingsPanel can={can} />
            )}

            {tab === "leave-requests" && can("manage_attendance") && (
              <LeaveRequestsPanel can={can} />
            )}

            {tab === "reports" && can("view_receipts") && (
              <ReportsPanel can={can} />
            )}

            {tab === "calendar" && can("manage_calendar") && (
              <CalendarPanel can={can} />
            )}

            {tab === "school-calendar" && can("manage_calendar") && (
              <SchoolCalendarPdfPanel can={can} />
            )}
          </>
        )}
      </main>
      </div>

      {selected && (
        <AdmissionDetailDrawer
          row={selected}
          onClose={() => setSelected(null)}
          onStatus={can("edit_admissions") ? updateAdmissionStatus : null}
        />
      )}

      {showChangePw && (
        <ChangePasswordModal onClose={() => setShowChangePw(false)} />
      )}
    </div>
  );
}

/* ===== Sub-components ===== */

function Overview({ stats, admissions, visits, leaves, onJumpToLeaves, canLeaves }) {
  if (!stats) return null;
  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-5" data-testid="admin-overview">
      {/* Stat cards */}
      <StatCard
        col="lg:col-span-3"
        icon={<Users size={20} />}
        accent="#648DE5"
        label="Admissions enquiries"
        value={stats.admissions.total}
        delta={`${stats.admissions.week} this week`}
      />
      <StatCard
        col="lg:col-span-3"
        icon={<CalendarDays size={20} />}
        accent="#5BC4B8"
        label="Campus visits"
        value={stats.visits.total}
        delta={`${stats.visits.week} this week`}
      />
      <StatCard
        col="lg:col-span-3"
        icon={<TrendingUp size={20} />}
        accent="#C6D947"
        label="Visits / Enquiries"
        value={`${stats.conversion_pct}%`}
        delta="Conversion proxy"
      />
      <StatCard
        col="lg:col-span-3"
        icon={<Mail size={20} />}
        accent="#3AB0D6"
        label="Newsletter"
        value={stats.newsletter.total}
        delta={`${stats.contact.total} contact msgs`}
      />

      {/* Parent messages (leave requests) */}
      {canLeaves && (
        <div className="lg:col-span-12 bg-[#FDFDF9] rounded-3xl p-6 border border-[#0F1B47]/8" data-testid="overview-parent-messages">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-xs font-bold tracking-[0.18em] uppercase text-[#648DE5]">Parent messages</div>
              <div className="font-display text-xl text-[#0F1B47] font-medium mt-0.5">Leave requests</div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[11px] font-bold tracking-wide uppercase rounded-full px-3 py-1.5 bg-[#C6D947]/30 text-[#0F1B47]">
                {(leaves || []).filter((l) => l.status === "pending").length} pending
              </span>
              <button
                onClick={onJumpToLeaves}
                className="text-[12px] font-semibold text-[#0F1B47] px-3.5 py-1.5 rounded-full bg-[#0F1B47]/5 hover:bg-[#0F1B47]/10"
                data-testid="overview-leaves-view-all"
              >
                View all
              </button>
            </div>
          </div>
          {(!leaves || leaves.length === 0) ? (
            <div className="bg-[#F5F5F0] rounded-2xl p-8 text-center">
              <MessageSquare size={26} className="mx-auto text-[#648DE5] mb-2" />
              <div className="text-[#0F1B47] font-medium text-sm">No parent messages yet</div>
              <p className="text-xs text-[#718096] mt-1">When parents submit leave requests from their portal, they'll appear here.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-3">
              {leaves.slice(0, 4).map((l) => (
                <div
                  key={l.id}
                  onClick={onJumpToLeaves}
                  className="bg-[#F5F5F0] rounded-2xl p-4 border border-[#0F1B47]/6 cursor-pointer hover:bg-[#0F1B47]/5 transition-colors"
                  data-testid={`overview-leave-${l.id}`}
                >
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="text-sm font-semibold text-[#0F1B47]">{l.student_name}</div>
                    <span className={`text-[9px] font-bold tracking-[0.16em] uppercase rounded-full px-2 py-0.5 border ${
                      l.status === "pending" ? "bg-[#C6D947]/30 text-[#0F1B47] border-[#C6D947]/60" :
                      l.status === "approved" ? "bg-emerald-50 text-emerald-700 border-emerald-200" :
                      "bg-red-50 text-red-700 border-red-200"
                    }`}>{l.status}</span>
                  </div>
                  <div className="text-xs text-[#4A5568] mt-1.5">
                    <strong className="text-[#0F1B47]">{l.days} day{l.days === 1 ? "" : "s"}</strong> from {fmtDate(l.start_date)} · {l.parent_name || l.parent_email}
                  </div>
                  <div className="text-[12px] text-[#0F1B47] mt-1 line-clamp-1">"{l.reason}"</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Upcoming birthdays */}
      <BirthdaysCard />

      {/* Recent enquiries */}
      <div className="lg:col-span-12 bg-[#FDFDF9] rounded-3xl p-6 border border-[#0F1B47]/8">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-xs font-bold tracking-[0.18em] uppercase text-[#648DE5]">Recent</div>
            <div className="font-display text-xl text-[#0F1B47] font-medium mt-0.5">Latest enquiries</div>
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-3">
          {admissions.slice(0, 4).map((a) => (
            <div key={a.id} className="bg-[#F5F5F0] rounded-2xl p-4 flex items-start gap-3 border border-[#0F1B47]/6">
              <ShapeCircle size={36} color="#648DE5" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-[#0F1B47] truncate">{a.student_name}</div>
                <div className="text-xs text-[#4A5568] truncate">
                  {PROGRAM_LABELS[a.program] || a.program} · {a.email}
                </div>
                <div className="text-[10px] text-[#718096] mt-1">{fmtDate(a.created_at)}</div>
              </div>
              <StatusPill status={a.status || "new"} />
            </div>
          ))}
          {admissions.length === 0 && (
            <div className="text-sm text-[#4A5568] py-4">No enquiries yet.</div>
          )}
        </div>
      </div>
    </div>
  );
}

function BirthdaysCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    adminApi.get("/admin/birthdays", { params: { days: 14 } })
      .then(({ data }) => { if (!cancelled) setData(data); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const items = data?.birthdays || [];
  const today = items.filter((b) => b.days_away === 0);
  const next = items.filter((b) => b.days_away > 0);

  return (
    <div className="lg:col-span-12 bg-[#FDFDF9] rounded-3xl p-6 border border-[#0F1B47]/8" data-testid="birthdays-card">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-2xl bg-[#C6D947]/30 flex items-center justify-center text-xl">🎂</div>
          <div>
            <div className="text-xs font-bold tracking-[0.18em] uppercase text-[#648DE5]">Upcoming · 14 days</div>
            <div className="font-display text-xl text-[#0F1B47] font-medium mt-0.5">Birthdays</div>
          </div>
        </div>
        <div className="text-[11px] text-[#718096]">{loading ? "Loading…" : `${items.length} ${items.length === 1 ? "birthday" : "birthdays"} ahead`}</div>
      </div>
      {!loading && items.length === 0 ? (
        <div className="text-sm text-[#4A5568] py-2">No birthdays in the next two weeks.</div>
      ) : (
        <div className="grid gap-2">
          {today.length > 0 && (
            <div className="bg-[#C6D947]/20 border border-[#C6D947]/40 rounded-2xl p-3">
              <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-[#5a6a14] mb-2">Today 🎉</div>
              <div className="grid sm:grid-cols-2 gap-2">
                {today.map((b) => <BirthdayRow key={`${b.kind}-${b.id}`} b={b} />)}
              </div>
            </div>
          )}
          {next.length > 0 && (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {next.slice(0, 9).map((b) => <BirthdayRow key={`${b.kind}-${b.id}`} b={b} />)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function BirthdayRow({ b }) {
  const initials = (b.name || "?").split(/\s+/).slice(0, 2).map((s) => s[0]).join("").toUpperCase();
  const niceDate = (() => {
    const d = new Date(b.next_iso);
    return d.toLocaleDateString(undefined, { day: "numeric", month: "short" });
  })();
  return (
    <div className="bg-[#F5F5F0] rounded-2xl p-3 flex items-center gap-3 border border-[#0F1B47]/6" data-testid={`birthday-${b.kind}-${b.id}`}>
      <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0 bg-[#FDFDF9] border border-[#0F1B47]/10 flex items-center justify-center">
        {b.photo_url ? (
          <img src={b.photo_url} alt={b.name} className="w-full h-full object-cover" />
        ) : (
          <span className="text-[#0F1B47] font-semibold text-[12px]">{initials}</span>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[13px] font-semibold text-[#0F1B47] truncate flex items-center gap-1.5">
          {b.name}
          <span className={`text-[8px] font-bold tracking-[0.14em] uppercase rounded-full px-1.5 py-0.5 ${b.kind === "staff" ? "bg-[#648DE5]/15 text-[#0F1B47]" : "bg-[#5BC4B8]/20 text-[#0c6e64]"}`}>{b.kind}</span>
        </div>
        <div className="text-[11px] text-[#4A5568] truncate">
          {b.subtitle}{b.turning ? ` · turning ${b.turning}` : ""}
        </div>
      </div>
      <div className="text-right shrink-0">
        <div className="text-[12px] font-semibold text-[#0F1B47]">{niceDate}</div>
        <div className="text-[10px] text-[#718096]">{b.days_away === 0 ? "Today" : `In ${b.days_away} day${b.days_away === 1 ? "" : "s"}`}</div>
      </div>
    </div>
  );
}

function StatCard({ col, icon, accent, label, value, delta }) {
  return (
    <div className={`bg-[#FDFDF9] rounded-3xl p-6 border border-[#0F1B47]/8 ${col || ""}`} data-testid={`stat-${label.toLowerCase().replace(/\s+/g,'-')}`}>
      <div className="flex items-center justify-between mb-4">
        <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: accent + "22", color: accent }}>
          {icon}
        </div>
      </div>
      <div className="text-xs font-bold tracking-[0.18em] uppercase text-[#648DE5]">{label}</div>
      <div className="font-display text-3xl md:text-4xl font-semibold text-[#0F1B47] mt-1 leading-none">{value}</div>
      <div className="text-xs text-[#4A5568] mt-2 font-light">{delta}</div>
    </div>
  );
}

function SectionWrap({ title, subtitle, onExport, search, setSearch, statuses, statusFilter, setStatusFilter, children }) {
  return (
    <div className="bg-[#FDFDF9] rounded-3xl border border-[#0F1B47]/8 overflow-hidden" data-testid="admin-section">
      <div className="p-6 md:p-7 flex flex-col md:flex-row md:items-center md:justify-between gap-4 border-b border-[#0F1B47]/8">
        <div>
          <div className="font-display text-2xl text-[#0F1B47] font-medium">{title}</div>
          <p className="text-sm text-[#4A5568] font-light mt-1">{subtitle}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {setSearch && (
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#4A5568]" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search…"
                className="bg-[#F5F5F0] border border-[#0F1B47]/12 rounded-full pl-9 pr-4 py-2 text-sm text-[#0F1B47] focus:outline-none focus:border-[#648DE5] w-56"
                data-testid="admin-search"
              />
            </div>
          )}
          {statuses && (
            <div className="relative inline-flex items-center gap-1 bg-[#F5F5F0] border border-[#0F1B47]/12 rounded-full px-3 py-2 text-sm">
              <Filter size={13} className="text-[#4A5568]" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="bg-transparent text-[#0F1B47] font-semibold focus:outline-none"
                data-testid="admin-status-filter"
              >
                <option value="all">All statuses</option>
                {statuses.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
          )}
          {onExport && (
            <button
              onClick={onExport}
              className="inline-flex items-center gap-2 bg-[#0F1B47] text-[#FDFDF9] rounded-full px-4 py-2 text-sm font-semibold hover:bg-[#1a2b6b]"
              data-testid="admin-export"
            >
              <Download size={14} />
              Export CSV
            </button>
          )}
        </div>
      </div>
      <div>{children}</div>
    </div>
  );
}

function AdmissionsTable({ rows, onSelect, onStatus }) {
  if (rows.length === 0) {
    return <div className="p-10 text-center text-sm text-[#4A5568]">No matching enquiries.</div>;
  }
  return (
    <div className="overflow-x-auto" data-testid="admissions-table">
      <table className="min-w-full text-sm">
        <thead className="bg-[#F5F5F0] text-[#0F1B47]">
          <tr className="text-[10px] font-bold tracking-[0.18em] uppercase text-left">
            <th className="px-5 py-3">Date</th>
            <th className="px-5 py-3">Student</th>
            <th className="px-5 py-3">Program</th>
            <th className="px-5 py-3">Parent</th>
            <th className="px-5 py-3">Contact</th>
            <th className="px-5 py-3">Source</th>
            <th className="px-5 py-3">Status</th>
            <th className="px-5 py-3 text-right">Open</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-t border-[#0F1B47]/8 hover:bg-[#F5F5F0]/60">
              <td className="px-5 py-3 text-[#4A5568]">{fmtDate(r.created_at)}</td>
              <td className="px-5 py-3 font-semibold text-[#0F1B47]">
                {r.student_name}
                <div className="text-[11px] text-[#718096] font-normal">{r.child_dob}</div>
              </td>
              <td className="px-5 py-3 text-[#0F1B47]">{PROGRAM_LABELS[r.program] || r.program}</td>
              <td className="px-5 py-3 text-[#0F1B47]">
                {r.father_name}
                <div className="text-[11px] text-[#718096]">{r.mother_name}</div>
              </td>
              <td className="px-5 py-3 text-[#0F1B47]">
                <div>{r.email}</div>
                <div className="text-[11px] text-[#718096]">{r.country_code} {r.mobile}</div>
              </td>
              <td className="px-5 py-3 text-[#4A5568] text-xs">{r.source}</td>
              <td className="px-5 py-3">
                <select
                  value={r.status || "new"}
                  onChange={(e) => onStatus && onStatus(r.id, e.target.value)}
                  disabled={!onStatus}
                  className={`text-xs font-semibold rounded-full border px-3 py-1.5 ${STATUS_COLORS[r.status || "new"] || STATUS_COLORS.new} ${onStatus ? "" : "opacity-70 cursor-not-allowed"}`}
                  data-testid={`adm-status-${r.id}`}
                >
                  {ADM_STATUSES.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </td>
              <td className="px-5 py-3 text-right">
                <button
                  onClick={() => onSelect(r)}
                  className="inline-flex items-center gap-1 text-[#0F1B47] font-semibold text-xs hover:underline"
                  data-testid={`adm-open-${r.id}`}
                >
                  Details <ChevronRight size={14} />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function VisitsTable({ rows, onStatus }) {
  if (rows.length === 0) {
    return <div className="p-10 text-center text-sm text-[#4A5568]">No visit bookings.</div>;
  }
  return (
    <div className="overflow-x-auto" data-testid="visits-table">
      <table className="min-w-full text-sm">
        <thead className="bg-[#F5F5F0] text-[#0F1B47]">
          <tr className="text-[10px] font-bold tracking-[0.18em] uppercase text-left">
            <th className="px-5 py-3">Booked</th>
            <th className="px-5 py-3">Visit date</th>
            <th className="px-5 py-3">Slot</th>
            <th className="px-5 py-3">Parent</th>
            <th className="px-5 py-3">Contact</th>
            <th className="px-5 py-3">Notes</th>
            <th className="px-5 py-3">Status</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-t border-[#0F1B47]/8 hover:bg-[#F5F5F0]/60">
              <td className="px-5 py-3 text-[#4A5568]">{fmtDate(r.created_at)}</td>
              <td className="px-5 py-3 font-semibold text-[#0F1B47]">{r.visit_date}</td>
              <td className="px-5 py-3 text-[#0F1B47]">{r.time_slot}</td>
              <td className="px-5 py-3 text-[#0F1B47]">{r.parent_name}</td>
              <td className="px-5 py-3 text-[#0F1B47]">
                <div>{r.email}</div>
                <div className="text-[11px] text-[#718096]">{r.phone}</div>
              </td>
              <td className="px-5 py-3 text-[#4A5568] text-xs max-w-[300px] truncate">{r.notes}</td>
              <td className="px-5 py-3">
                <select
                  value={r.status || "confirmed"}
                  onChange={(e) => onStatus && onStatus(r.id, e.target.value)}
                  disabled={!onStatus}
                  className={`text-xs font-semibold rounded-full border px-3 py-1.5 ${STATUS_COLORS[r.status || "confirmed"] || STATUS_COLORS.confirmed} ${onStatus ? "" : "opacity-70 cursor-not-allowed"}`}
                  data-testid={`vis-status-${r.id}`}
                >
                  {VISIT_STATUSES.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SimpleTable({ rows, columns, testid }) {
  if (rows.length === 0) {
    return <div className="p-10 text-center text-sm text-[#4A5568]">Nothing here yet.</div>;
  }
  return (
    <div className="overflow-x-auto" data-testid={testid}>
      <table className="min-w-full text-sm">
        <thead className="bg-[#F5F5F0] text-[#0F1B47]">
          <tr className="text-[10px] font-bold tracking-[0.18em] uppercase text-left">
            {columns.map((c) => (
              <th key={c.key} className="px-5 py-3">{c.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id || r.email} className="border-t border-[#0F1B47]/8 hover:bg-[#F5F5F0]/60">
              {columns.map((c) => (
                <td key={c.key} className={`px-5 py-3 text-[#0F1B47] ${c.clamp ? "max-w-[440px] truncate" : ""}`}>
                  {c.fmt ? c.fmt(r[c.key]) : r[c.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function StatusPill({ status }) {
  return (
    <span className={`inline-block text-[10px] font-bold tracking-[0.16em] uppercase rounded-full px-2.5 py-1 border ${STATUS_COLORS[status] || STATUS_COLORS.new}`}>
      {status}
    </span>
  );
}

function AdmissionDetailDrawer({ row, onClose, onStatus }) {
  return (
    <div className="fixed inset-0 z-50 flex" data-testid="admission-drawer">
      <button
        className="absolute inset-0 bg-[#0F1B47]/40 backdrop-blur-sm"
        onClick={onClose}
        aria-label="Close"
      />
      <div className="relative ml-auto w-full max-w-xl h-full bg-[#FDFDF9] overflow-y-auto p-7 md:p-9 animate-in slide-in-from-right">
        <div className="flex items-start justify-between mb-6">
          <div>
            <div className="text-xs font-bold tracking-[0.18em] uppercase text-[#648DE5]">Enquiry</div>
            <h3 className="font-display text-2xl text-[#0F1B47] font-medium mt-1">{row.student_name}</h3>
          </div>
          <button onClick={onClose} className="text-sm font-semibold text-[#0F1B47] underline" data-testid="drawer-close">
            Close
          </button>
        </div>
        <div className="mb-5">
          <select
            value={row.status || "new"}
            onChange={(e) => onStatus && onStatus(row.id, e.target.value)}
            disabled={!onStatus}
            className={`text-xs font-bold tracking-[0.18em] uppercase rounded-full border px-4 py-2 ${STATUS_COLORS[row.status || "new"] || STATUS_COLORS.new} ${onStatus ? "" : "opacity-70 cursor-not-allowed"}`}
          >
            {ADM_STATUSES.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>
        <div className="space-y-1 text-sm">
          <DetailRow k="Created" v={fmtDate(row.created_at)} />
          <DetailRow k="Academic year" v={row.academic_year} />
          <DetailRow k="Program" v={PROGRAM_LABELS[row.program] || row.program} />
          <DetailRow k="Preferred start" v={row.preferred_start} />
          <DetailRow k="Date of birth" v={row.child_dob} />
          <DetailRow k="Gender" v={row.gender} />
          <DetailRow k="Nationality" v={row.nationality} />
          <DetailRow k="Languages at home" v={row.mother_tongue} />
          <DetailRow k="Medical / sensory notes" v={row.medical_notes} />
          <DetailRow k="Father" v={`${row.father_name} — ${row.father_occupation || ""}`} />
          <DetailRow k="Mother" v={`${row.mother_name} — ${row.mother_occupation || ""}`} />
          <DetailRow k="Email" v={row.email} />
          <DetailRow k="Mobile" v={`${row.country_code} ${row.mobile}`} />
          <DetailRow k="Alternate phone" v={row.alt_phone} />
          <DetailRow k="Address" v={row.address} />
          <DetailRow k="Source" v={row.source} />
          <DetailRow k="Referred by" v={row.referred_by} />
          <DetailRow k="Message" v={row.message} />
        </div>
      </div>
    </div>
  );
}

function DetailRow({ k, v }) {
  if (!v) return null;
  return (
    <div className="grid grid-cols-3 gap-3 py-2 border-b border-[#0F1B47]/6">
      <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-[#648DE5] mt-0.5">{k}</div>
      <div className="col-span-2 text-[#0F1B47]">{v}</div>
    </div>
  );
}

function fmtDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
}

/* ===== Staff management (superadmin only) ===== */

const PERMISSION_DEFS = [
  { key: "view_admissions", label: "View admissions", group: "Admissions" },
  { key: "edit_admissions", label: "Edit admission status", group: "Admissions" },
  { key: "view_visits", label: "View campus visits", group: "Visits" },
  { key: "edit_visits", label: "Edit visit status", group: "Visits" },
  { key: "view_students", label: "View students list", group: "Students" },
  { key: "edit_students", label: "Add / edit students", group: "Students" },
  { key: "manage_attendance", label: "Mark daily attendance", group: "Students" },
  { key: "manage_documents", label: "View / upload student documents", group: "Students" },
  { key: "manage_term_reports", label: "Upload term reports (PDF)", group: "Students" },
  { key: "view_updates", label: "View weekly updates (read-only)", group: "Students" },
  { key: "manage_updates", label: "Post / edit weekly updates", group: "Students" },
  { key: "view_receipts", label: "View fees & receipts", group: "Fees" },
  { key: "manage_fees", label: "Generate fees & record payments", group: "Fees" },
  { key: "generate_receipts", label: "Generate / delete fee receipts", group: "Fees" },
  { key: "manage_menu", label: "Edit weekly food menu", group: "Other" },
  { key: "manage_newsletter", label: "Upload monthly newsletter", group: "Other" },
  { key: "view_newsletter", label: "View newsletter list", group: "Other" },
  { key: "view_messages", label: "View contact messages", group: "Other" },
  { key: "export_csv", label: "Export CSV files", group: "Other" },
  { key: "export_attendance", label: "Export staff attendance (Excel)", group: "Other" },
];
const EMPTY_PERMS = PERMISSION_DEFS.reduce((a, p) => ({ ...a, [p.key]: false }), {});

function StaffPanel({ me, staff, onRefresh }) {
  const [editingId, setEditingId] = useState(null);
  const [adding, setAdding] = useState(false);
  const [tempCreds, setTempCreds] = useState(null); // {email, temp_password}

  return (
    <div className="bg-[#FDFDF9] rounded-3xl border border-[#0F1B47]/8 overflow-hidden" data-testid="staff-panel">
      <div className="p-6 md:p-7 border-b border-[#0F1B47]/8 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <h2 className="font-display text-2xl text-[#0F1B47] font-medium">Staff & permissions</h2>
          <p className="text-sm text-[#4A5568] mt-1 max-w-2xl">
            Add staff members and choose what they can view or edit. Only superadmins
            can manage staff. The superadmin role grants every permission automatically.
          </p>
        </div>
        <button
          onClick={() => { setAdding(true); setEditingId(null); }}
          className="inline-flex items-center gap-2 bg-[#0F1B47] text-[#FDFDF9] rounded-full px-5 py-2.5 font-semibold text-sm hover:bg-[#1a2b6b] transition-colors"
          data-testid="staff-add-btn"
        >
          <UserPlus size={15} />
          Add staff member
        </button>
      </div>

      {tempCreds && (
        <TempCredsBanner creds={tempCreds} onClose={() => setTempCreds(null)} />
      )}

      {adding && (
        <StaffForm
          mode="create"
          onCancel={() => setAdding(false)}
          onSaved={(res) => {
            setAdding(false);
            if (res?.temp_password) setTempCreds({ email: res.email, temp_password: res.temp_password });
            onRefresh();
          }}
        />
      )}

      <ul data-testid="staff-list">
        {staff.map((s) => (
          <li key={s.id} className="border-t border-[#0F1B47]/8">
            {editingId === s.id ? (
              <StaffForm
                mode="edit"
                initial={s}
                isSelf={s.id === me.id}
                onCancel={() => setEditingId(null)}
                onSaved={() => { setEditingId(null); onRefresh(); }}
                onResetPassword={async () => {
                  try {
                    const res = await adminApi.post(`/admin/staff/${s.id}/reset-password`);
                    setTempCreds({ email: res.data.email, temp_password: res.data.temp_password });
                    toast.success("Temporary password generated.");
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || "Reset failed.");
                  }
                }}
              />
            ) : (
              <StaffRow
                staff={s}
                isSelf={s.id === me.id}
                onEdit={() => setEditingId(s.id)}
                onDelete={async () => {
                  if (!window.confirm(`Remove ${s.email}? This cannot be undone.`)) return;
                  try {
                    await adminApi.delete(`/admin/staff/${s.id}`);
                    toast.success("Staff member removed.");
                    onRefresh();
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || "Delete failed.");
                  }
                }}
                onToggleDisabled={async () => {
                  try {
                    await adminApi.patch(`/admin/staff/${s.id}`, { disabled: !s.disabled });
                    toast.success(s.disabled ? "Account enabled." : "Account disabled.");
                    onRefresh();
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || "Update failed.");
                  }
                }}
              />
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

function StaffRow({ staff, isSelf, onEdit, onDelete, onToggleDisabled }) {
  const grantedPerms = Object.entries(staff.permissions || {}).filter(([, v]) => v).map(([k]) => k);
  const isSuper = staff.role === "superadmin";
  const photoInitials = (staff.name || "?").split(/\s+/).slice(0, 2).map((s) => s[0]).join("").toUpperCase();
  return (
    <div className="p-5 md:p-6 flex flex-col md:flex-row md:items-center gap-4" data-testid={`staff-row-${staff.id}`}>
      <div className="flex items-start gap-3 flex-1 min-w-0">
        {staff.photo_url ? (
          <img src={staff.photo_url} alt={staff.name} className="w-12 h-12 rounded-2xl object-cover bg-[#F5F5F0] flex-shrink-0" loading="lazy" decoding="async" />
        ) : (
          <div className="w-12 h-12 rounded-2xl bg-[#E3EDF9] text-[#0F1B47] font-semibold flex items-center justify-center flex-shrink-0">{photoInitials}</div>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-semibold text-[#0F1B47] text-[15px]">{staff.name}</div>
            {staff.employee_id && (
              <span className="text-[10px] font-mono text-[#718096]">{staff.employee_id}</span>
            )}
            {isSuper ? (
              <span className="inline-flex items-center gap-1 text-[10px] font-bold tracking-[0.16em] uppercase px-2 py-0.5 rounded-full bg-[#0F1B47] text-[#FDFDF9]">
                <Shield size={10} /> Superadmin
              </span>
            ) : (
              <span className="text-[10px] font-bold tracking-[0.16em] uppercase px-2 py-0.5 rounded-full bg-[#648DE5]/15 text-[#0F1B47]">Staff</span>
            )}
            {staff.disabled && (
              <span className="text-[10px] font-bold tracking-[0.16em] uppercase px-2 py-0.5 rounded-full bg-red-100 text-red-700">Disabled</span>
            )}
            {isSelf && (
              <span className="text-[10px] font-bold tracking-[0.16em] uppercase px-2 py-0.5 rounded-full bg-[#C6D947]/30 text-[#0F1B47]">You</span>
            )}
          </div>
          <div className="text-sm text-[#4A5568] mt-0.5 truncate">
            {staff.designation && <span>{staff.designation}{staff.department ? ` · ${staff.department}` : ""} · </span>}
            {staff.email}
            {staff.mobile && <span className="text-[#718096]"> · {staff.mobile}</span>}
          </div>
          {staff.classes?.length > 0 && (
            <div className="text-xs text-[#4A5568] mt-1">Class teacher: <span className="font-medium">{staff.classes.join(", ")}</span></div>
          )}
          <div className="text-xs text-[#718096] mt-1">
            {isSuper ? "All permissions" : (grantedPerms.length === 0 ? "No permissions yet" : `${grantedPerms.length} permission${grantedPerms.length === 1 ? "" : "s"} granted`)}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <button
          onClick={onEdit}
          className="text-xs font-semibold text-[#0F1B47] px-3 py-2 rounded-full bg-[#0F1B47]/5 hover:bg-[#0F1B47]/10"
          data-testid={`staff-edit-${staff.id}`}
        >
          Edit
        </button>
        {!isSelf && (
          <>
            <button
              onClick={onToggleDisabled}
              className="text-xs font-semibold text-[#0F1B47] px-3 py-2 rounded-full bg-[#0F1B47]/5 hover:bg-[#0F1B47]/10"
              data-testid={`staff-toggle-${staff.id}`}
            >
              {staff.disabled ? "Enable" : "Disable"}
            </button>
            <button
              onClick={onDelete}
              className="text-xs font-semibold text-red-700 px-3 py-2 rounded-full bg-red-50 hover:bg-red-100 inline-flex items-center gap-1"
              data-testid={`staff-delete-${staff.id}`}
            >
              <Trash2 size={12} />
              Remove
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function StaffForm({ mode, initial, isSelf, onCancel, onSaved, onResetPassword }) {
  const [email, setEmail] = useState(initial?.email || "");
  const [name, setName] = useState(initial?.name || "");
  const [role, setRole] = useState(initial?.role || "admin");
  const [perms, setPerms] = useState({ ...EMPTY_PERMS, ...(initial?.permissions || {}) });
  const [photoUrl, setPhotoUrl] = useState(initial?.photo_url || "");
  const [employeeId, setEmployeeId] = useState(initial?.employee_id || "");
  const [designation, setDesignation] = useState(initial?.designation || "");
  const [department, setDepartment] = useState(initial?.department || "");
  const [mobile, setMobile] = useState(initial?.mobile || "");
  const [dob, setDob] = useState(initial?.dob || "");
  const [classes, setClasses] = useState(initial?.classes || []);
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const photoFileRef = React.useRef(null);

  const initials = (name || initial?.email || "?")
    .split(/\s+|@/).filter(Boolean).slice(0, 2).map((s) => s[0]).join("").toUpperCase();

  const handlePhotoFile = async (file) => {
    if (!file) return;
    if (!/^image\/(jpe?g|png|webp)$/i.test(file.type) && !/\.(jpe?g|png|webp)$/i.test(file.name)) {
      toast.error("Please pick a JPG, PNG or WEBP image.");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Image is over 8 MB. Pick a smaller one.");
      return;
    }
    // Existing staff → upload to server right away; new staff → hold as data URL
    if (mode === "edit" && initial?.id) {
      setUploadingPhoto(true);
      try {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await adminApi.post(`/admin/staff/${initial.id}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        setPhotoUrl(data.photo_url || "");
        toast.success("Photo updated.");
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Upload failed");
      } finally { setUploadingPhoto(false); }
    } else {
      const reader = new FileReader();
      reader.onload = () => setPhotoUrl(String(reader.result || ""));
      reader.onerror = () => toast.error("Could not read file");
      reader.readAsDataURL(file);
    }
  };

  const clearPhoto = async () => {
    if (mode === "edit" && initial?.id && photoUrl) {
      try { await adminApi.delete(`/admin/staff/${initial.id}/photo`); } catch {/* ignore */}
    }
    setPhotoUrl("");
  };

  const togglePerm = (k) => setPerms((p) => ({ ...p, [k]: !p[k] }));
  const toggleClass = (c) => {
    setClasses((cs) => {
      const next = cs.includes(c) ? cs.filter((x) => x !== c) : [...cs, c];
      // When this person becomes a class teacher, auto-enable the perms they need to do their job.
      if (next.length > 0 && role === "admin") {
        setPerms((p) => ({
          ...p,
          view_students: p.view_students || true,
          manage_attendance: p.manage_attendance || true,
          view_updates: p.view_updates || true,
        }));
      }
      return next;
    });
  };
  const ALL_CLASSES = ["Toddlers", "Early Years 1", "Early Years 2", "Early Years 3"];

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const profile = {
        photo_url: photoUrl.trim(),
        employee_id: employeeId.trim(),
        designation: designation.trim(),
        department: department.trim(),
        mobile: mobile.trim(),
        dob: dob.trim(),
        classes,
      };
      if (mode === "create") {
        const res = await adminApi.post("/admin/staff", {
          email: email.trim(),
          name: name.trim(),
          role,
          permissions: role === "superadmin" ? null : perms,
          ...profile,
        });
        toast.success("Staff member added.");
        onSaved(res.data);
      } else {
        const body = {
          name: name.trim(),
          role,
          ...profile,
        };
        if (role !== "superadmin") body.permissions = perms;
        await adminApi.patch(`/admin/staff/${initial.id}`, body);
        toast.success("Staff updated.");
        onSaved();
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const groups = ["Admissions", "Visits", "Students", "Fees", "Other"];

  return (
    <form onSubmit={submit} className="bg-[#F5F5F0] p-5 md:p-7" data-testid="staff-form">
      <div className="grid md:grid-cols-2 gap-4 mb-5">
        <div>
          <label className="block text-[11px] font-bold tracking-[0.16em] uppercase text-[#648DE5] mb-2">Full name</label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            placeholder="e.g. Oormila R V"
            className="w-full bg-[#FDFDF9] border border-[#0F1B47]/12 rounded-2xl px-4 py-2.5 text-[14.5px] focus:outline-none focus:border-[#648DE5]"
            data-testid="staff-form-name"
          />
        </div>
        <div>
          <label className="block text-[11px] font-bold tracking-[0.16em] uppercase text-[#648DE5] mb-2">Email</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            disabled={mode === "edit"}
            placeholder="staff@naaviearlyyears.edu.in"
            className="w-full bg-[#FDFDF9] border border-[#0F1B47]/12 rounded-2xl px-4 py-2.5 text-[14.5px] focus:outline-none focus:border-[#648DE5] disabled:opacity-70"
            data-testid="staff-form-email"
          />
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-4 mb-5">
        <div>
          <label className="block text-[11px] font-bold tracking-[0.16em] uppercase text-[#648DE5] mb-2">Designation</label>
          <input value={designation} onChange={(e) => setDesignation(e.target.value)} placeholder="e.g. Teacher" className="w-full bg-[#FDFDF9] border border-[#0F1B47]/12 rounded-2xl px-4 py-2.5 text-[14.5px] focus:outline-none focus:border-[#648DE5]" data-testid="staff-form-designation" />
        </div>
        <div>
          <label className="block text-[11px] font-bold tracking-[0.16em] uppercase text-[#648DE5] mb-2">Department</label>
          <input value={department} onChange={(e) => setDepartment(e.target.value)} placeholder="e.g. Academics" className="w-full bg-[#FDFDF9] border border-[#0F1B47]/12 rounded-2xl px-4 py-2.5 text-[14.5px] focus:outline-none focus:border-[#648DE5]" />
        </div>
        <div>
          <label className="block text-[11px] font-bold tracking-[0.16em] uppercase text-[#648DE5] mb-2">Mobile</label>
          <input value={mobile} onChange={(e) => setMobile(e.target.value)} placeholder="e.g. 9876543210" className="w-full bg-[#FDFDF9] border border-[#0F1B47]/12 rounded-2xl px-4 py-2.5 text-[14.5px] focus:outline-none focus:border-[#648DE5]" />
        </div>
        <div>
          <label className="block text-[11px] font-bold tracking-[0.16em] uppercase text-[#648DE5] mb-2">Date of birth</label>
          <input type="date" value={dob} onChange={(e) => setDob(e.target.value)} className="w-full bg-[#FDFDF9] border border-[#0F1B47]/12 rounded-2xl px-4 py-2.5 text-[14.5px] focus:outline-none focus:border-[#648DE5]" data-testid="staff-form-dob" />
        </div>
        <div className="md:col-span-2">
          <label className="block text-[11px] font-bold tracking-[0.16em] uppercase text-[#648DE5] mb-2">Photo</label>
          <div className="flex items-center gap-3" data-testid="staff-form-photo">
            <div className="w-16 h-16 rounded-2xl bg-[#F5F5F0] border border-[#0F1B47]/8 overflow-hidden flex-shrink-0 flex items-center justify-center">
              {photoUrl ? (
                <img src={photoUrl} alt={name || "staff"} className="w-full h-full object-cover" />
              ) : (
                <span className="text-[#0F1B47] font-semibold text-base">{initials}</span>
              )}
            </div>
            <input ref={photoFileRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={(e) => handlePhotoFile(e.target.files?.[0])} className="hidden" data-testid="staff-form-photo-file" />
            <button type="button" onClick={() => photoFileRef.current?.click()} disabled={uploadingPhoto} className="inline-flex items-center gap-1.5 bg-[#0F1B47] text-[#FDFDF9] rounded-full px-3.5 py-1.5 font-semibold text-[12px] hover:bg-[#1a2b6b] disabled:opacity-50" data-testid="staff-form-photo-upload">
              {uploadingPhoto ? "Uploading…" : photoUrl ? "Replace" : "Upload"}
            </button>
            {photoUrl && (
              <button type="button" onClick={clearPhoto} disabled={uploadingPhoto} className="inline-flex items-center gap-1 text-[11px] font-semibold text-red-600 px-2 py-1 rounded-full hover:bg-red-50" data-testid="staff-form-photo-remove">
                Remove
              </button>
            )}
            <span className="text-[10px] text-[#718096] ml-1">JPG, PNG or WEBP · max 8 MB</span>
          </div>
        </div>
        <div>
          <label className="block text-[11px] font-bold tracking-[0.16em] uppercase text-[#648DE5] mb-2">Employee ID</label>
          <input value={employeeId} onChange={(e) => setEmployeeId(e.target.value)} placeholder={mode === "create" ? "Auto: EMP/N" : ""} className="w-full bg-[#FDFDF9] border border-[#0F1B47]/12 rounded-2xl px-4 py-2.5 text-[14.5px] focus:outline-none focus:border-[#648DE5]" />
        </div>
      </div>

      <div className="mb-5">
        <label className="block text-[11px] font-bold tracking-[0.16em] uppercase text-[#648DE5] mb-2">Class teacher of</label>
        <div className="flex flex-wrap gap-2">
          {ALL_CLASSES.map((c) => (
            <button
              type="button"
              key={c}
              onClick={() => toggleClass(c)}
              className={`text-xs font-semibold rounded-full px-3.5 py-1.5 border transition-colors ${
                classes.includes(c)
                  ? "bg-[#0F1B47] text-[#FDFDF9] border-[#0F1B47]"
                  : "bg-[#FDFDF9] text-[#0F1B47] border-[#0F1B47]/15 hover:border-[#0F1B47]/40"
              }`}
              data-testid={`staff-form-class-${c.replace(/\s+/g, "-").toLowerCase()}`}
            >
              {c}
            </button>
          ))}
        </div>
        {role === "admin" && classes.length > 0 && (!perms.manage_attendance || !perms.view_students) && (
          <div className="mt-3 rounded-2xl border border-amber-300 bg-amber-50 text-amber-900 px-4 py-3 text-[13px]" data-testid="staff-form-attendance-warning">
            <b>Heads up:</b> A class teacher must have{" "}
            {!perms.view_students && <code className="px-1.5 py-0.5 rounded bg-white/70 mx-0.5">View students &amp; ledgers</code>}
            {!perms.view_students && !perms.manage_attendance && " and "}
            {!perms.manage_attendance && <code className="px-1.5 py-0.5 rounded bg-white/70 mx-0.5">Mark daily attendance</code>}
            {" "}enabled to take their class&apos;s attendance.
            <button
              type="button"
              onClick={() => setPerms((p) => ({ ...p, view_students: true, manage_attendance: true }))}
              className="ml-2 underline font-semibold"
              data-testid="staff-form-attendance-fix"
            >
              Enable both
            </button>
          </div>
        )}
      </div>

      <div className="mb-5">
        <label className="block text-[11px] font-bold tracking-[0.16em] uppercase text-[#648DE5] mb-2">Role</label>
        <div className="flex flex-col sm:flex-row gap-2">
          {[
            { v: "admin", title: "Staff", desc: "Selected permissions only" },
            { v: "superadmin", title: "Superadmin", desc: "Full access · can manage other staff" },
          ].map((r) => (
            <label
              key={r.v}
              className={`flex-1 cursor-pointer rounded-2xl border-2 px-4 py-3 transition-all ${
                role === r.v
                  ? "border-[#0F1B47] bg-[#FDFDF9]"
                  : "border-transparent bg-[#FDFDF9]/60 hover:border-[#0F1B47]/15"
              } ${isSelf && r.v !== "superadmin" ? "opacity-50 cursor-not-allowed" : ""}`}
            >
              <input
                type="radio"
                name="role"
                value={r.v}
                checked={role === r.v}
                onChange={() => !(isSelf && r.v !== "superadmin") && setRole(r.v)}
                disabled={isSelf && r.v !== "superadmin"}
                className="sr-only"
                data-testid={`staff-form-role-${r.v}`}
              />
              <div className="font-semibold text-[#0F1B47] text-sm">{r.title}</div>
              <div className="text-[12px] text-[#4A5568] mt-0.5">{r.desc}</div>
            </label>
          ))}
        </div>
        {isSelf && (
          <p className="text-[12px] text-[#718096] mt-2">You can't downgrade your own superadmin role.</p>
        )}
      </div>

      {role === "admin" && (
        <div className="mb-5">
          <div className="flex items-center justify-between mb-3">
            <label className="block text-[11px] font-bold tracking-[0.16em] uppercase text-[#648DE5]">Permissions</label>
            <div className="flex gap-2">
              <button type="button" onClick={() => setPerms({ ...EMPTY_PERMS, view_admissions: true, view_visits: true, view_newsletter: true, view_messages: true, view_students: true, view_receipts: true })} className="text-[11px] font-semibold text-[#0F1B47] underline">
                View-only preset
              </button>
              <button type="button" onClick={() => setPerms({ ...EMPTY_PERMS, view_admissions: true, edit_admissions: true, view_visits: true, edit_visits: true })} className="text-[11px] font-semibold text-[#0F1B47] underline">
                Admissions team preset
              </button>
              <button type="button" onClick={() => setPerms(PERMISSION_DEFS.reduce((a, p) => ({ ...a, [p.key]: true }), {}))} className="text-[11px] font-semibold text-[#0F1B47] underline">
                Grant all
              </button>
            </div>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            {groups.map((g) => (
              <div key={g} className="bg-[#FDFDF9] rounded-2xl p-4 border border-[#0F1B47]/8">
                <div className="text-[11px] font-bold tracking-[0.16em] uppercase text-[#0F1B47] mb-2">{g}</div>
                <ul className="space-y-2">
                  {PERMISSION_DEFS.filter((p) => p.group === g).map((p) => (
                    <li key={p.key}>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={!!perms[p.key]}
                          onChange={() => togglePerm(p.key)}
                          className="w-4 h-4 accent-[#0F1B47]"
                          data-testid={`staff-form-perm-${p.key}`}
                        />
                        <span className="text-[13.5px] text-[#0F1B47]">{p.label}</span>
                      </label>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <button
          type="submit"
          disabled={saving}
          className="bg-[#0F1B47] text-[#FDFDF9] rounded-full px-5 py-2.5 font-semibold text-sm hover:bg-[#1a2b6b] disabled:opacity-50 transition-colors"
          data-testid="staff-form-save"
        >
          {saving ? "Saving…" : (mode === "create" ? "Create staff" : "Save changes")}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="text-sm font-semibold text-[#0F1B47] px-4 py-2 rounded-full bg-[#0F1B47]/5 hover:bg-[#0F1B47]/10"
          data-testid="staff-form-cancel"
        >
          Cancel
        </button>
        {mode === "edit" && onResetPassword && !isSelf && (
          <button
            type="button"
            onClick={onResetPassword}
            className="ml-auto text-sm font-semibold text-[#0F1B47] px-4 py-2 rounded-full bg-[#C6D947]/40 hover:bg-[#C6D947]/60 inline-flex items-center gap-2"
            data-testid="staff-form-reset"
          >
            <KeyRound size={14} />
            Reset password
          </button>
        )}
      </div>
    </form>
  );
}

function TempCredsBanner({ creds, onClose }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(creds.temp_password);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {}
  };
  return (
    <div className="bg-[#C6D947]/30 border-y-2 border-[#C6D947] px-6 py-4" data-testid="staff-temp-creds">
      <div className="flex flex-col md:flex-row md:items-center gap-3">
        <div className="flex-1">
          <div className="text-[11px] font-bold tracking-[0.18em] uppercase text-[#0F1B47]">
            Temporary password — share securely
          </div>
          <div className="text-sm text-[#0F1B47] mt-1">
            <span className="font-semibold">{creds.email}</span> can sign in with the password below.
            Ask them to change it after their first login.
          </div>
          <div className="mt-2 inline-flex items-center gap-2 bg-[#FDFDF9] border border-[#0F1B47]/15 rounded-xl px-3 py-1.5">
            <code className="font-mono text-[14px] text-[#0F1B47]">{creds.temp_password}</code>
            <button
              type="button"
              onClick={copy}
              className="text-[#0F1B47] hover:text-[#1a2b6b]"
              aria-label="Copy password"
              data-testid="staff-temp-copy"
            >
              {copied ? <Check size={14} /> : <Copy size={14} />}
            </button>
          </div>
        </div>
        <button
          onClick={onClose}
          className="text-xs font-semibold text-[#0F1B47] px-3 py-2 rounded-full bg-[#0F1B47]/5 hover:bg-[#0F1B47]/10 self-start"
          data-testid="staff-temp-dismiss"
        >
          Dismiss
        </button>
      </div>
    </div>
  );
}

function ChangePasswordModal({ onClose }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (next.length < 8) return setError("New password must be at least 8 characters");
    if (next !== confirm) return setError("Passwords don't match");
    setSaving(true);
    try {
      await adminApi.post("/admin/change-password", {
        current_password: current,
        new_password: next,
      });
      toast.success("Password updated. Use the new one next time you sign in.");
      onClose();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Failed";
      setError(msg);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#0F1B47]/40 flex items-center justify-center p-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="bg-[#FDFDF9] rounded-3xl w-full max-w-md p-6 shadow-2xl" data-testid="change-password-modal">
        <h3 className="font-display text-xl text-[#0F1B47] font-medium mb-1">Change your password</h3>
        <p className="text-sm text-[#4A5568] mb-5">Pick something at least 8 characters long.</p>
        <div className="space-y-4">
          <div>
            <label className="block text-[11px] font-bold tracking-[0.16em] uppercase text-[#0F1B47] mb-1.5">Current password</label>
            <input
              type="password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              required
              autoComplete="current-password"
              className="w-full bg-[#F5F5F0] border border-[#0F1B47]/12 rounded-2xl px-4 py-2.5 text-[14.5px] focus:outline-none focus:border-[#648DE5]"
              data-testid="change-pw-current"
            />
          </div>
          <div>
            <label className="block text-[11px] font-bold tracking-[0.16em] uppercase text-[#0F1B47] mb-1.5">New password</label>
            <input
              type="password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              required
              minLength={8}
              autoComplete="new-password"
              className="w-full bg-[#F5F5F0] border border-[#0F1B47]/12 rounded-2xl px-4 py-2.5 text-[14.5px] focus:outline-none focus:border-[#648DE5]"
              data-testid="change-pw-new"
            />
          </div>
          <div>
            <label className="block text-[11px] font-bold tracking-[0.16em] uppercase text-[#0F1B47] mb-1.5">Confirm new password</label>
            <input
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
              minLength={8}
              autoComplete="new-password"
              className="w-full bg-[#F5F5F0] border border-[#0F1B47]/12 rounded-2xl px-4 py-2.5 text-[14.5px] focus:outline-none focus:border-[#648DE5]"
              data-testid="change-pw-confirm"
            />
          </div>
          {error && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-2xl px-4 py-2">{error}</div>}
        </div>
        <div className="flex items-center gap-3 mt-5">
          <button type="submit" disabled={saving} className="bg-[#0F1B47] text-[#FDFDF9] rounded-full px-5 py-2.5 font-semibold text-sm hover:bg-[#1a2b6b] disabled:opacity-50" data-testid="change-pw-save">
            {saving ? "Saving…" : "Update password"}
          </button>
          <button type="button" onClick={onClose} className="text-sm font-semibold text-[#0F1B47] px-4 py-2 rounded-full bg-[#0F1B47]/5 hover:bg-[#0F1B47]/10">Cancel</button>
        </div>
      </form>
    </div>
  );
}

